import {
  Alert,
  Box,
  Button,
  Checkbox,
  Group,
  List,
  Modal,
  Stack,
  Text,
  TextInput,
  useMantineTheme,
} from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import { useState } from 'react'

interface DeleteChannelModalProps {
  opened: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  channelName: string
  isLoading?: boolean
}

/**
 * Double-confirmation modal for permanently deleting a channel (sponsor).
 *
 * Requires the user to:
 *   1. Tick a checkbox acknowledging the action.
 *   2. Type the exact channel name as a second guard against accidental clicks.
 *
 * This pattern (checkbox + type-to-confirm) is the industry standard for
 * irreversible destructive actions (cf. Stripe account closure, Atlassian
 * workspace deletion, GitHub repository deletion).
 *
 * The confirm button is labeled "Delete & log out" because the caller is
 * expected to terminate the session immediately after a successful delete.
 */
export function DeleteChannelModal({
  opened,
  onClose,
  onConfirm,
  channelName,
  isLoading = false,
}: DeleteChannelModalProps) {
  const theme = useMantineTheme()
  const trimmedChannelName = channelName.trim()
  const [agreed, setAgreed] = useState(false)
  const [typedName, setTypedName] = useState('')

  const [prevOpened, setPrevOpened] = useState(opened)

  if (opened !== prevOpened) {
    setPrevOpened(opened)
    if (!opened) {
      setAgreed(false)
      setTypedName('')
    }
  }

  const nameMatches = typedName.trim() === trimmedChannelName && trimmedChannelName.length > 0
  const canConfirm = agreed && nameMatches && !isLoading

  const handleClose = () => {
    if (isLoading) return
    onClose()
  }

  return (
    <Modal.Root opened={opened} onClose={handleClose} centered size="lg" closeOnClickOutside={!isLoading}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <IconAlertTriangle
            size={24}
            style={{ minWidth: 24, color: 'var(--mantine-color-red-6)', marginRight: theme.spacing.xs }}
          />
          <Modal.Title fw={600}>Confirm deletion?</Modal.Title>
          <Modal.CloseButton disabled={isLoading} />
        </Modal.Header>
        <Modal.Body pt="xs">
          <Stack gap="lg">
            <Text size="sm">
              You are about to permanently delete{' '}
              <Text span fw={600}>
                {trimmedChannelName}
              </Text>
              . Confirm the deletion below.
            </Text>

            <Alert variant="light" color="red" icon={<IconAlertTriangle size={18} />} title="This cannot be undone">
              <List size="sm" spacing={4}>
                <List.Item>The channel and all of its content will be deactivated.</List.Item>
                <List.Item>End users will lose access immediately.</List.Item>
                <List.Item>You will be logged out as soon as deletion completes.</List.Item>
              </List>
            </Alert>

            <Checkbox
              checked={agreed}
              onChange={event => setAgreed(event.currentTarget.checked)}
              disabled={isLoading}
              label={
                <Text size="sm">
                  I agree to delete{' '}
                  <Text span fw={600}>
                    {trimmedChannelName}
                  </Text>
                </Text>
              }
            />

            <Box>
              <Text size="sm" mb={6}>
                Type{' '}
                <Text span fw={600}>
                  {trimmedChannelName}
                </Text>{' '}
                to confirm deletion:
              </Text>
              <TextInput
                value={typedName}
                onChange={event => setTypedName(event.currentTarget.value)}
                placeholder={trimmedChannelName}
                disabled={isLoading}
                error={typedName.length > 0 && !nameMatches ? 'Channel name does not match.' : undefined}
                autoComplete="off"
                spellCheck={false}
                styles={{ input: { fontFamily: 'monospace' } }}
              />
            </Box>

            <Group justify="flex-end" gap="sm" mt="sm">
              <Button variant="default" onClick={handleClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button color="red" onClick={() => void onConfirm()} disabled={!canConfirm} loading={isLoading}>
                Delete &amp; Log Out
              </Button>
            </Group>
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  )
}
